import { useCallback, useEffect, useState } from 'react';
import { Plus, Edit2, Trash2, Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../components/Modal';
import { ImageUploadField } from '../components/ImageUploadField';
import { blogService, type BlogCategoryOption } from '../services/blogService';
import { fieldClass, labelClass, slugify } from '../utils/form';
import { rowNumber } from '../utils/rowNumber';

type BlogRow = {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image: string;
  author: string;
  tags: string[];
  categoryId: string;
  categoryName: string;
	isPublished: boolean;
  publishedAt?: string;
  viewCount: number;
};

type BlogFormState = {
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  image: string;
  author: string;
  tagsText: string;
  categoryId: string;
  isPublished: boolean;
};

const emptyForm = (): BlogFormState => ({
  title: '',
  slug: '',
  excerpt: '',
  content: '',
  image: '',
  author: 'تحریریه ایوا',
  tagsText: '',
  categoryId: '',
  isPublished: true,
});

function asTags(value: unknown): string[] {
  if (Array.isArray(value)) return value.map((t) => String(t)).filter(Boolean);
  if (typeof value === 'string' && value.trim()) {
    try {
      const parsed = JSON.parse(value) as unknown;
      if (Array.isArray(parsed)) return parsed.map((t) => String(t)).filter(Boolean);
    } catch {
      return value.split(',').map((t) => t.trim()).filter(Boolean);
    }
  }
  return [];
}

function mapPost(raw: Record<string, unknown>): BlogRow {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    slug: String(raw.slug ?? ''),
    excerpt: String(raw.excerpt ?? ''),
    content: String(raw.content ?? ''),
    image: String(raw.image ?? ''),
    author: String(raw.author ?? 'تحریریه ایوا'),
    tags: asTags(raw.tags),
    categoryId: String(raw.categoryId ?? ''),
    categoryName: String(raw.categoryName ?? ''),
    isPublished: raw.isPublished !== false,
    publishedAt: raw.publishedAt ? String(raw.publishedAt) : undefined,
    viewCount: Number(raw.viewCount ?? raw.views ?? 0) || 0,
  };
}

function formFromRow(row: BlogRow): BlogFormState {
  return {
    title: row.title,
    slug: row.slug,
    excerpt: row.excerpt,
    content: row.content,
    image: row.image,
    author: row.author,
    tagsText: row.tags.join('، '),
    categoryId: row.categoryId,
    isPublished: row.isPublished,
  };
}

function payloadFromForm(form: BlogFormState): Record<string, unknown> {
  const tags = form.tagsText
    .split(/[,،]/)
    .map((t) => t.trim())
    .filter(Boolean);
  return {
    title: form.title.trim(),
    slug: form.slug.trim() || slugify(form.title),
    excerpt: form.excerpt.trim(),
    content: form.content.trim(),
    image: form.image.trim(),
    author: form.author.trim() || 'تحریریه ایوا',
    tags,
    categoryId: form.categoryId ? Number(form.categoryId) : null,
    isPublished: form.isPublished,
  };
}

export function BlogPage() {
  const [posts, setPosts] = useState<BlogRow[]>([]);
  const [categories, setCategories] = useState<BlogCategoryOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formOpen, setFormOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [editing, setEditing] = useState<BlogRow | null>(null);
  const [viewing, setViewing] = useState<BlogRow | null>(null);
  const [form, setForm] = useState<BlogFormState>(emptyForm);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rawPosts, cats] = await Promise.all([blogService.list(), blogService.categories()]);
      setPosts(rawPosts.map(mapPost));
      setCategories(cats);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'خطا در دریافت مقالات');
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    setForm(emptyForm());
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm());
    setFormOpen(true);
  };

  const openEdit = (row: BlogRow) => {
    setEditing(row);
    setForm(formFromRow(row));
    setFormOpen(true);
  };

  const openView = (row: BlogRow) => {
    setViewing(row);
    setViewOpen(true);
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('عنوان مقاله الزامی است');
      return;
    }
    if (!form.content.trim()) {
      toast.error('متن مقاله الزامی است');
      return;
    }
    setSaving(true);
    try {
      const payload = payloadFromForm(form);
      if (editing) {
        await blogService.update(editing.id, payload);
        toast.success('مقاله به‌روزرسانی شد');
      } else {
        await blogService.create(payload);
        toast.success('مقاله ایجاد شد');
      }
      closeForm();
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'ذخیره مقاله ناموفق بود');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: BlogRow) => {
    if (!window.confirm(`مقاله «${row.title}» حذف شود؟`)) return;
    try {
      await blogService.remove(row.id);
      toast.success('مقاله حذف شد');
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'حذف ناموفق بود');
    }
  };

  const setField = <K extends keyof BlogFormState>(key: K, value: BlogFormState[K]) => {
    setForm((prev) => {
      const next = { ...prev, [key]: value };
      if (key === 'title' && !editing && !prev.slug) {
        next.slug = slugify(String(value));
      }
      return next;
    });
  };

  return (
    <div className="space-y-6" dir="rtl">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title text-xl font-bold text-slate-900 sm:text-3xl">مقالات</h1>
          <p className="text-slate-600 mt-1">مدیریت محتوای وبلاگ فروشگاه ایوا</p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="w-full sm:w-auto flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors font-medium"
        >
          <Plus className="h-4 w-4" />
          افزودن مقاله
        </button>
      </div>

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-500">
          در حال بارگذاری...
        </div>
      ) : posts.length === 0 ? (
        <div className="rounded-lg border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          هنوز مقاله‌ای ثبت نشده. اولین مطلب وبلاگ را اضافه کنید.
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          <div className="admin-table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-right font-medium">ردیف</th>
                <th className="px-4 py-3 text-right font-medium">مقاله</th>
                <th className="px-4 py-3 text-right font-medium">دسته</th>
                <th className="px-4 py-3 text-right font-medium">بازدید</th>
                <th className="px-4 py-3 text-right font-medium">وضعیت</th>
                <th className="px-4 py-3 text-right font-medium">عملیات</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {posts.map((post, index) => (
                <tr key={post.id} className="hover:bg-slate-50/80">
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-500">{rowNumber(index)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {post.image ? (
                        <img src={post.image} alt="" className="h-12 w-16 rounded-lg object-cover" />
                      ) : (
                        <div className="flex h-12 w-16 items-center justify-center rounded-lg bg-slate-100 text-xs text-slate-400">
                          —
                        </div>
                      )}
                      <div>
                        <div className="font-semibold text-slate-900">{post.title}</div>
                        <div className="text-xs text-slate-500 mt-0.5 line-clamp-1">{post.excerpt || post.slug}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-600">{post.categoryName || '—'}</td>
                  <td className="px-4 py-3 text-sm tabular-nums text-slate-600">
                    <span className="inline-flex items-center gap-1">
                      <Eye className="h-3.5 w-3.5 text-slate-400" />
                      {post.viewCount.toLocaleString('fa-IR')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`rounded-full px-2.5 py-1 text-xs font-medium ${
                        post.isPublished ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {post.isPublished ? 'منتشر شده' : 'پیش‌نویس'}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => openView(post)}
                        className="rounded-lg border border-slate-200 p-2 text-slate-600 hover:bg-slate-50"
                        title="مشاهده"
                      >
                        <Eye className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => openEdit(post)}
                        className="rounded-lg border border-slate-200 p-2 text-blue-600 hover:bg-blue-50"
                        title="ویرایش"
                      >
                        <Edit2 className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => void handleDelete(post)}
                        className="rounded-lg border border-slate-200 p-2 text-red-600 hover:bg-red-50"
                        title="حذف"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      <Modal
        isOpen={formOpen}
        onClose={closeForm}
        title={editing ? 'ویرایش مقاله' : 'افزودن مقاله'}
        size="lg"
        footer={
          <>
            <button
              type="button"
              onClick={closeForm}
              disabled={saving}
              className="px-4 py-2 rounded-lg border border-slate-300 text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              انصراف
            </button>
            <button
              type="button"
              onClick={() => void handleSave()}
              disabled={saving}
              className="px-4 py-2 rounded-lg bg-indigo-600 text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {saving ? 'در حال ذخیره...' : 'ذخیره'}
            </button>
          </>
        }
      >
        <div className="space-y-4" dir="rtl">
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>عنوان</label>
              <input
                className={fieldClass}
                value={form.title}
                onChange={(e) => setField('title', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>اسلاگ</label>
              <input
                className={fieldClass}
                value={form.slug}
                onChange={(e) => setField('slug', e.target.value)}
                dir="ltr"
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>خلاصه</label>
            <textarea
              className={fieldClass}
              rows={2}
              value={form.excerpt}
              onChange={(e) => setField('excerpt', e.target.value)}
            />
          </div>
          <div>
            <label className={labelClass}>متن کامل</label>
            <textarea
              className={fieldClass}
              rows={8}
              value={form.content}
              onChange={(e) => setField('content', e.target.value)}
            />
          </div>
          <ImageUploadField
            label="تصویر شاخص"
            value={form.image}
            onChange={(url) => setField('image', url)}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className={labelClass}>نویسنده</label>
              <input
                className={fieldClass}
                value={form.author}
                onChange={(e) => setField('author', e.target.value)}
              />
            </div>
            <div>
              <label className={labelClass}>دسته بلاگ</label>
              <select
                className={fieldClass}
                value={form.categoryId}
                onChange={(e) => setField('categoryId', e.target.value)}
              >
                <option value="">بدون دسته</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className={labelClass}>برچسب‌ها (با ویرگول)</label>
            <input
              className={fieldClass}
              value={form.tagsText}
              onChange={(e) => setField('tagsText', e.target.value)}
              placeholder="نگهداری، هدیه، گل"
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-slate-700">
            <input
              type="checkbox"
              checked={form.isPublished}
              onChange={(e) => setField('isPublished', e.target.checked)}
            />
            منتشر شده
          </label>
        </div>
      </Modal>

      <Modal
        isOpen={viewOpen}
        onClose={() => {
          setViewOpen(false);
          setViewing(null);
        }}
        title={viewing?.title || 'مشاهده مقاله'}
        size="lg"
      >
        {viewing && (
          <div className="space-y-4" dir="rtl">
            {viewing.image ? (
              <img src={viewing.image} alt="" className="h-48 w-full rounded-xl object-cover" />
            ) : null}
            <p className="text-sm text-slate-600">{viewing.excerpt}</p>
            <div className="whitespace-pre-wrap text-sm leading-7 text-slate-800">{viewing.content}</div>
            <div className="flex flex-wrap gap-2 text-xs text-slate-500">
              <span>نویسنده: {viewing.author}</span>
              {viewing.categoryName ? <span>دسته: {viewing.categoryName}</span> : null}
              {viewing.tags.map((t) => (
                <span key={t} className="rounded-full bg-slate-100 px-2 py-0.5">
                  {t}
                </span>
              ))}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}
